import { Address, Location } from "../../../../services";
import { ProviderGroup } from "../../../../constants/ProviderGroupFormInitialValues";
import { useGetProviderGroupProfile } from "../../../../queries/admin/provider-group/use-get-provider-group-profile";
import { useLocation } from "../../../../queries/use-location";
import { getStoredRealm } from "@/utils/auth-storage";

/**
 * Place-of-Service values that represent a Telehealth encounter. For these,
 * the Service Location is optional and the Billing Address / Service State are
 * resolved from the Provider Group Profile when no location is selected
 * (ECRH-5370).
 */
const TELEHEALTH_PLACES_OF_SERVICE = [
  "TELEHEALTH_INSIDE_PATIENT_HOME",
  "TELEHEALTH_OUTSIDE_PATIENT_HOME",
];

export const isTelehealthPlaceOfService = (
  placeOfService?: string | null
): boolean =>
  !!placeOfService && TELEHEALTH_PLACES_OF_SERVICE.includes(placeOfService);

export interface ResolvedServiceLocationBilling {
  /** 2-letter service state derived from the resolved Billing Address. */
  serviceState?: string;
  /** Billing Address to populate in the Claim / CMS-1500 Box 32 / Invoice. */
  serviceLocationAddress?: Address;
  /**
   * `true` once the underlying source (selected Location, or Provider Group
   * Profile when no location) has loaded, so callers can avoid clobbering
   * existing form values with an unresolved `undefined`.
   */
  isResolved: boolean;
}

/**
 * Picks the address a billing document should be built from.
 *
 * A Location (Settings -> Practice -> Location) and the Provider Group Profile
 * both hold a Physical Address and a Billing Address, and the Billing Address is
 * commonly left empty when it is the same as the physical one. Falling back to
 * the Physical Address keeps the Service State resolvable in that case instead
 * of leaving the field blank.
 */
const resolveBillingAddress = (
  billingAddress?: Address,
  physicalAddress?: Address
): Address | undefined =>
  billingAddress?.state ? billingAddress : (physicalAddress ?? billingAddress);

/**
 * Resolves the Billing Address (and hence Service State) used for a billing
 * document:
 *   - when a Service Location is selected -> that Location's Billing Address
 *     (Settings -> Practice -> Location)
 *   - when no Service Location is selected -> the Provider Group Profile
 *     Billing Address (Settings -> Practice -> Profile)
 *
 * The selected Location is fetched by id so resolution works regardless of the
 * Service Location dropdown's current search/pagination page.
 */
export function useServiceLocationBilling(
  locationUuid?: string
): ResolvedServiceLocationBilling {
  const realm = getStoredRealm() ?? "";

  const { data: rawLocation } = useLocation({
    locationId: locationUuid ?? "",
    providerGroupId: realm,
  });

  const { data: providerGroupProfile } = useGetProviderGroupProfile(
    realm || undefined,
    false
  );

  if (locationUuid) {
    // `useLocation` is generically mistyped as a paginated result; the by-id
    // endpoint actually returns a single Location.
    const location = rawLocation?.data as unknown as Location | undefined;
    const billingAddress: Address | undefined = resolveBillingAddress(
      location?.billingAddress,
      location?.physicalAddress
    );
    return {
      serviceState: billingAddress?.state,
      serviceLocationAddress: billingAddress,
      isResolved: !!location,
    };
  }

  const profile = providerGroupProfile as unknown as ProviderGroup | undefined;
  const billingAddress = resolveBillingAddress(
    profile?.billingAddress as Address | undefined,
    profile?.physicalAddress as Address | undefined
  );
  return {
    serviceState: billingAddress?.state,
    serviceLocationAddress: billingAddress,
    isResolved: !!profile,
  };
}
